import { z } from 'zod';
import { GraphNodeLabel } from './node-labels.js';
import { GraphSourceTool } from './source-tool.js';
/**
 * A cited source — either a graph node (preferred, when the fact is
 * derived from a node we ingested) or a Firestore doc (when the fact
 * is from a LeanOpx-native artifact like a Rock or Note that hasn't
 * been mirrored to the graph yet). The Ask / L10-brief / People-
 * evidence surfaces render `<Citation>` inline against either.
 *
 * INTERP-tagged claims (LLM interpretation, not a grounded fact) carry
 * `interp: true` so the UI can mark them distinctly from grounded
 * citations — the CLAUDE.md rule is "No orphan claims, ever."
 */
export const CITATION_KINDS = ['graph-node', 'graph-edge', 'firestore-doc'];
export const CitationKind = z.enum(CITATION_KINDS);
export const Citation = z.object({
    kind: CitationKind,
    /** For graph-node: the Cypher label. For firestore-doc: the collection. */
    label: z.string().min(1),
    /** Node id (graph) or doc id (firestore). */
    id: z.string().min(1),
    /** Optional originating tool — `slack` / `salesforce` / etc. for graph nodes. */
    source: GraphSourceTool.optional(),
    /** Human-friendly anchor text for the citation chip. */
    title: z.string().min(1),
    /** When true: the cited row is an LLM interpretation, not a grounded fact. */
    interp: z.boolean().default(false),
    /** Optional URL to open in a new tab (e.g. Slack permalink). */
    url: z.string().url().optional(),
});
/**
 * Helper: build a Citation for a graph node. Co-located with the schema
 * so call sites don't reinvent the shape.
 */
export function citationForNode(label, id, title, source) {
    return {
        kind: 'graph-node',
        label,
        id,
        title,
        interp: false,
        ...(source ? { source } : {}),
    };
}
/** Helper: build a Citation for a Firestore doc. */
export function citationForDoc(collection, id, title) {
    return {
        kind: 'firestore-doc',
        label: collection,
        id,
        title,
        interp: false,
    };
}
